import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileLayout } from '../../components/MobileLayout';
import { Tabs } from '../../components/ui/Tabs';
import { Clock, MapPin, User, ChevronRight, ChevronLeft, X, Check, Home, Send, BellRing, Map as MapIcon, CalendarDays, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useScreenInit } from '../../useScreenInit.js';
import { usePlatform, Job } from '../../context/PlatformContext';
import { findInterventionId } from './appartamentiData';
// ---- Status meta ---------------------------------------------------------
function getJobStatusMeta(job: Job) {
  if (job.status === 'da_assegnare') {
    if (job.assignment?.phase === 'inviata') {
      return {
        label: 'In attesa',
        cls: 'bg-amber-50 text-amber-700 border-amber-200'
      };
    }
    if (job.assignment?.phase === 'rifiutata') {
      return {
        label: 'Rifiutata',
        cls: 'bg-red-50 text-red-700 border-red-200'
      };
    }
    return {
      label: 'Da assegnare',
      cls: 'bg-amber-50 text-amber-700 border-amber-200'
    };
  }
  const map: Record<string, {
    label: string;
    cls: string;
  }> = {
    assegnata: {
      label: 'Assegnata',
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    in_corso: {
      label: '● In corso',
      cls: 'bg-coral/5 text-coral border-coral/20'
    },
    completata: {
      label: 'Completata',
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    }
  };
  return map[job.status];
}
function formatAge(ms: number) {
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'da pochi istanti';
  if (mins < 60) return `da ${mins}m`;
  return `da ${Math.floor(mins / 60)}h`;
}
// ---- Date helpers --------------------------------------------------------
// Job.date uses relative labels ("Oggi", "Domani", "Ieri"). To populate the
// week and month layouts we map every job to a concrete date: known relative
// labels resolve around "today"; anything else is spread deterministically
// across the current month using a stable hash of the job id.
const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const DAY_NAMES_FULL = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MONTH_NAMES_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function hashString(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = h * 31 + s.charCodeAt(i) | 0;
  return Math.abs(h);
}
function getJobDate(job: Job, today: Date): Date {
  const label = job.date?.toLowerCase().trim();
  if (label === 'oggi') return startOfDay(today);
  if (label === 'domani') return startOfDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  if (label === 'ieri') return startOfDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1));
  // Unknown label -> stable day within the current month
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const day = hashString(job.id) % daysInMonth + 1;
  return new Date(today.getFullYear(), today.getMonth(), day);
}
type ViewMode = 'lista' | 'mappa';
type Period = 'oggi' | 'settimana' | 'mese';
const PERIOD_LABELS: Record<Period, string> = {
  oggi: 'Oggi',
  settimana: 'Settimana',
  mese: 'Mese'
};
// Short zone label, e.g. "Milano · Navigli" -> "Navigli"
function shortZone(zone: string): string {
  const parts = zone.split('·');
  return (parts[parts.length - 1] || zone).trim();
}
export function CmIncarichiPage() {
  const screenInit = useScreenInit() as {
    tab?: string;
  };
  const navigate = useNavigate();
  const {
    jobs,
    cleaners,
    sendAssignment,
    sendReminder,
    activeLoad,
    pendingAssignments,
    assignmentAge
  } = usePlatform();
  const [period, setPeriod] = useState<Period>('oggi');
  const [periodOpen, setPeriodOpen] = useState(false);
  const periodRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState(screenInit?.tab ?? 'attivi');
  const [assignFor, setAssignFor] = useState<Job | null>(null);
  // Calendar month shown in the "Mese" view
  const today = useMemo(() => startOfDay(new Date()), []);
  const [monthDate, setMonthDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedCalendarDay, setSelectedCalendarDay] = useState<Date>(today);
  // Force re-render every 5s to update ages
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(timer);
  }, []);
  // Close the period popover on outside click / Escape
  useEffect(() => {
    if (!periodOpen) return;
    const onClick = (e: MouseEvent) => {
      if (periodRef.current && !periodRef.current.contains(e.target as Node)) {
        setPeriodOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPeriodOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [periodOpen]);
  // Tab filter (status) applied everywhere
  const byTab = useMemo(() => jobs.filter(j => tab === 'attivi' ? j.status !== 'completata' : j.status === 'completata'), [jobs, tab]);
  // Decorate jobs with a concrete date
  const datedJobs = useMemo(() => byTab.map(j => ({
    job: j,
    date: getJobDate(j, today)
  })), [byTab, today]);
  // Current week (Mon–Sun) containing today
  const weekDays = useMemo(() => {
    const dow = today.getDay(); // 0=Sun
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset);
    return Array.from({
      length: 7
    }, (_, i) => {
      const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
      return d;
    });
  }, [today]);
  // Jobs scoped to the active period (drives BOTH list and map views)
  const periodJobs = useMemo(() => {
    if (period === 'oggi') {
      return datedJobs.filter(d => sameDay(d.date, today)).map(d => d.job);
    }
    if (period === 'settimana') {
      const start = weekDays[0];
      const end = weekDays[6];
      return datedJobs.filter(d => d.date >= start && d.date <= end).map(d => d.job);
    }
    // mese -> jobs within the shown month
    return datedJobs.filter(d => d.date.getFullYear() === monthDate.getFullYear() && d.date.getMonth() === monthDate.getMonth()).map(d => d.job);
  }, [datedJobs, period, today, weekDays, monthDate]);
  const pendingCount = pendingAssignments().length;
  // Cleaners that cover the job's zone (fallback: all) — never offer 'off' ones.
  const eligibleCleaners = useMemo(() => {
    if (!assignFor) return [];
    const inZone = cleaners.filter(c => c.availability !== 'off' && c.zones.some(z => z.toLowerCase() === assignFor.zone.toLowerCase()));
    return inZone.length > 0 ? inZone : cleaners.filter(c => c.availability !== 'off');
  }, [assignFor, cleaners]);
  const openJob = (j: Job) => {
    const interventoId = findInterventionId(j.apartmentId, j.date);
    navigate(interventoId ? `/cm/clienti/appartamenti/${j.apartmentId}/interventi/${interventoId}` : `/cm/clienti/appartamenti/${j.apartmentId}`);
  };
  const handleAssign = (cleanerId: string) => {
    if (!assignFor) return;
    sendAssignment(assignFor.id, cleanerId);
    setAssignFor(null);
  };
  return <MobileLayout data-id="element-1828">
      {/* Title row */}
      <div className="mb-5" data-id="element-1829">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1830">Incarichi</h1>
      </div>

      {/* Tab bar (left) + Periodo selector (right, same row/height) */}
      <div className="flex items-center justify-between gap-3 mb-6" data-id="element-1831">
        <Tabs value={tab} onValueChange={setTab} items={[{
        id: 'attivi',
        label: 'Attivi'
      }, {
        id: 'completati',
        label: 'Completati'
      }]} data-id="element-1832" />

        <div className="flex items-center gap-2 flex-shrink-0" ref={periodRef} data-id="element-1833">
          <span className="hidden sm:inline text-xs text-muted-foreground" data-id="element-1834">
            {periodJobs.length}{' '}
            {periodJobs.length === 1 ? 'incarico' : 'incarichi'}
          </span>
          <div className="relative" data-id="element-1835">
            <button type="button" onClick={() => setPeriodOpen(v => !v)} aria-haspopup="listbox" aria-expanded={periodOpen} className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-navy hover:border-gray-300 transition-colors shadow-sm" data-id="element-1836">
              <CalendarDays size={16} className="text-coral" data-id="element-1837" />
              <span className="hidden sm:inline text-muted-foreground" data-id="element-1838">
                Periodo:
              </span>
              <span data-id="element-1839">{PERIOD_LABELS[period]}</span>
              <ChevronDown size={16} className={`text-gray-400 transition-transform ${periodOpen ? 'rotate-180' : ''}`} data-id="element-1840" />
            </button>
            {periodOpen && <div role="listbox" className="absolute right-0 top-full mt-2 w-44 bg-white border border-border rounded-xl shadow-lg overflow-hidden z-30 animate-in fade-in zoom-in-95 duration-150" data-id="element-1841">
                {(['oggi', 'settimana', 'mese'] as Period[]).map(p => <button key={p} role="option" aria-selected={period === p} onClick={() => {
              setPeriod(p);
              setPeriodOpen(false);
            }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${period === p ? 'text-coral font-semibold bg-coral/5' : 'text-navy hover:bg-gray-50'}`} data-id="element-1842">
                    {PERIOD_LABELS[p]}
                    {period === p && <Check size={15} className="text-coral" data-id="element-1843" />}
                  </button>)}
              </div>}
          </div>
        </div>
      </div>

      {/* JOB LIST — operational view (Oggi / Settimana / Mese) */}
      <>
        {period === 'oggi' && <JobList jobs={periodJobs} tab={tab} periodLabel="oggi" onOpen={openJob} onAssign={setAssignFor} onReminder={id => sendReminder(id, 'manuale')} assignmentAge={assignmentAge} data-id="element-1844" />}

        {period === 'settimana' && <WeekColumns weekDays={weekDays} datedJobs={datedJobs} today={today} onOpen={openJob} onAssign={setAssignFor} onReminder={id => sendReminder(id, 'manuale')} data-id="element-1845" />}

        {period === 'mese' && <MonthCalendar monthDate={monthDate} onPrevMonth={() => {
        const next = new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1);
        setMonthDate(next);
        setSelectedCalendarDay(next.getFullYear() === today.getFullYear() && next.getMonth() === today.getMonth() ? today : next);
      }} onNextMonth={() => {
        const next = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
        setMonthDate(next);
        setSelectedCalendarDay(next.getFullYear() === today.getFullYear() && next.getMonth() === today.getMonth() ? today : next);
      }} datedJobs={datedJobs} today={today} selectedDay={selectedCalendarDay} onSelectDay={setSelectedCalendarDay} onOpen={openJob} onAssign={setAssignFor} onReminder={id => sendReminder(id, 'manuale')} data-id="element-1846" />}
      </>

      {/* Assign cleaner modal */}
      {assignFor && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAssignFor(null)} data-id="element-1847">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Assegna cleaner" data-id="element-1848">
            <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-1849">
              <div className="min-w-0" data-id="element-1850">
                <h2 className="text-lg font-bold text-navy" data-id="element-1851">Assegna cleaner</h2>
                <p className="text-xs text-muted-foreground truncate" data-id="element-1852">
                  {assignFor.apartmentName} · {assignFor.zone} ·{' '}
                  {assignFor.date}
                </p>
              </div>
              <button onClick={() => setAssignFor(null)} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1853">
                <X size={20} className="text-gray-500" data-id="element-1854" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2" data-id="element-1855">
              {eligibleCleaners.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6" data-id="element-1856">
                  Nessun cleaner disponibile per questa zona.
                </p> : eligibleCleaners.map(c => <button key={c.id} type="button" onClick={() => handleAssign(c.id)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-coral hover:bg-coral/5 transition-colors text-left" data-id="element-1857">
                    <span className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center text-xs font-bold text-coral flex-shrink-0" data-id="element-1858">
                      {c.name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('')}
                    </span>
                    <div className="flex-1 min-w-0" data-id="element-1859">
                      <div className="text-sm font-semibold text-navy truncate" data-id="element-1860">
                        {c.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate" data-id="element-1861">
                        {c.zones.join(', ')} · {activeLoad(c.id)} attivi
                      </div>
                    </div>
                    <Check size={18} className="text-coral flex-shrink-0" data-id="element-1862" />
                  </button>)}
            </div>
          </div>
        </div>}
    </MobileLayout>;
}
// ==========================================================================
// LIST (flat) — used for the "Oggi" period
// ==========================================================================
function JobList({
  jobs,
  tab,
  periodLabel,
  onOpen,
  onAssign,
  onReminder,
  assignmentAge
}: {
  jobs: Job[];
  tab: string;
  periodLabel: string;
  onOpen: (j: Job) => void;
  onAssign: (j: Job) => void;
  onReminder: (id: string) => void;
  assignmentAge: (id: string) => number | null;
}) {
  if (jobs.length === 0) {
    return <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-1863">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-1864">
          <Clock size={20} className="text-muted-foreground" data-id="element-1865" />
        </div>
        <p className="text-sm font-medium text-foreground" data-id="element-1866">
          {tab === 'attivi' ? 'Nessun incarico attivo' : 'Nessun incarico completato'}
        </p>
        <p className="text-xs text-muted-foreground mt-1" data-id="element-1867">
          Non ci sono incarichi per {periodLabel} in questa vista.
        </p>
      </div>;
  }
  return <div className="space-y-3" data-id="element-1868">
      {jobs.map(j => <JobCard key={j.id} job={j} onOpen={onOpen} onAssign={onAssign} onReminder={onReminder} assignmentAge={assignmentAge} data-id="element-1869" />)}
    </div>;
}
function JobCard({
  job: j,
  onOpen,
  onAssign,
  onReminder,
  assignmentAge
}: {
  job: Job;
  onOpen: (j: Job) => void;
  onAssign: (j: Job) => void;
  onReminder: (id: string) => void;
  assignmentAge: (id: string) => number | null;
}) {
  const meta = getJobStatusMeta(j);
  const isPending = j.status === 'da_assegnare' && j.assignment?.phase === 'inviata';
  const isRejected = j.status === 'da_assegnare' && j.assignment?.phase === 'rifiutata';
  const age = isPending ? assignmentAge(j.id) : null;
  return <Card className="hover:border-coral/40 hover:shadow-sm transition-all" data-id="element-1870">
      <CardContent className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => onOpen(j)} data-id="element-1871">
        <div className="min-w-0" data-id="element-1872">
          <div className="flex items-center gap-2 mb-1" data-id="element-1873">
            <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-1874">
              {j.apartmentName}
            </h3>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.cls}`} data-id="element-1875">
              {meta.label}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-id="element-1876">
            <span className="flex items-center gap-1" data-id="element-1877">
              <Home size={12} data-id="element-1878" />
              {j.type}
            </span>
            <span className="flex items-center gap-1" data-id="element-1879">
              <MapPin size={12} data-id="element-1880" />
              {j.zone}
            </span>
            <span className="flex items-center gap-1" data-id="element-1881">
              <Clock size={12} data-id="element-1882" />
              {j.date} · {j.time}
            </span>
            {j.cleanerName && <span className="flex items-center gap-1" data-id="element-1883">
                <User size={12} data-id="element-1884" />
                {j.cleanerName}
              </span>}
          </div>
        </div>
        <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" data-id="element-1885" />
      </CardContent>
      {j.status === 'da_assegnare' && <CardFooter data-id="element-1886">
          {isPending ? <>
              <span className="text-xs font-medium text-amber-700 flex items-center gap-1" data-id="element-1887">
                <Send size={12} data-id="element-1888" />
                In attesa di {j.assignment?.cleanerName}{' '}
                {age != null && `· ${formatAge(age)}`}
              </span>
              <Button size="sm" variant="outline" onClick={() => onReminder(j.id)} data-id="element-1889">
                <BellRing size={15} data-id="element-1890" />
                Sollecita
              </Button>
            </> : <>
              <span className="text-xs text-muted-foreground" data-id="element-1891">
                {isRejected ? `Rifiutata da ${j.assignment?.cleanerName}` : 'Nessun cleaner assegnato'}
              </span>
              <Button size="sm" onClick={() => onAssign(j)} data-id="element-1892">
                <Send size={15} data-id="element-1893" />
                Invia richiesta
              </Button>
            </>}
        </CardFooter>}
    </Card>;
}
// Compact job row used inside week columns / calendar day list
function JobChip({
  job: j,
  onOpen,
  onAssign,
  onReminder
}: {
  job: Job;
  onOpen: (j: Job) => void;
  onAssign?: (j: Job) => void;
  onReminder?: (id: string) => void;
}) {
  const meta = getJobStatusMeta(j);
  const isPending = j.status === 'da_assegnare' && j.assignment?.phase === 'inviata';
  const needsAssign = j.status === 'da_assegnare' && !isPending;
  return <div className="rounded-lg border border-border bg-white hover:border-coral/40 hover:shadow-sm transition-all" data-id="element-1894">
      <button type="button" onClick={() => onOpen(j)} className="w-full text-left p-2.5" data-id="element-1895">
        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-1896">
          <span className="text-xs font-semibold text-navy truncate" data-id="element-1897">
            {j.time}
          </span>
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border flex-shrink-0 ${meta.cls}`} data-id="element-1898">
            {meta.label}
          </span>
        </div>
        <p className="text-xs font-medium text-foreground truncate" data-id="element-1899">
          {j.apartmentName}
        </p>
        <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mt-0.5" data-id="element-1900">
          <MapPin size={10} data-id="element-1901" />
          {shortZone(j.zone)}
        </p>
      </button>
      {/* Inline assignment actions (available in all views) */}
      {j.status === 'da_assegnare' && (onAssign || onReminder) && <div className="px-2.5 pb-2.5 -mt-1" data-id="element-1902">
          {isPending ? onReminder && <button type="button" onClick={e => {
        e.stopPropagation();
        onReminder(j.id);
      }} className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-medium text-amber-700 border border-amber-200 bg-amber-50 rounded-md py-1 hover:bg-amber-100 transition-colors" data-id="element-1903">
                  <BellRing size={12} data-id="element-1904" />
                  Sollecita
                </button> : needsAssign && onAssign && <button type="button" onClick={e => {
        e.stopPropagation();
        onAssign(j);
      }} className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-medium text-white bg-coral rounded-md py-1 hover:bg-coral-600 transition-colors" data-id="element-1905">
                  <Send size={12} data-id="element-1906" />
                  Assegna
                </button>}
        </div>}
    </div>;
}
// ==========================================================================
// WEEK columns — Outlook-style, one column per day
// ==========================================================================
function WeekColumns({
  weekDays,
  datedJobs,
  today,
  onOpen,
  onAssign,
  onReminder
}: {
  weekDays: Date[];
  datedJobs: {
    job: Job;
    date: Date;
  }[];
  today: Date;
  onOpen: (j: Job) => void;
  onAssign: (j: Job) => void;
  onReminder: (id: string) => void;
}) {
  return <div className="flex gap-3 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-1907">
      {weekDays.map(day => {
      const dayJobs = datedJobs.filter(d => sameDay(d.date, day)).map(d => d.job).sort((a, b) => a.time.localeCompare(b.time));
      const isToday = sameDay(day, today);
      return <div key={day.toISOString()} className="flex-shrink-0 w-44 rounded-xl border border-border bg-gray-50/60" data-id="element-1908">
            {/* Column header */}
            <div className={`px-3 py-2.5 rounded-t-xl border-b border-border ${isToday ? 'bg-coral/5' : 'bg-white'}`} data-id="element-1909">
              <div className="flex items-center justify-between" data-id="element-1910">
                <span className={`text-[11px] uppercase tracking-wider font-semibold ${isToday ? 'text-coral' : 'text-gray-500'}`} data-id="element-1911">
                  {DAY_NAMES_SHORT[day.getDay()]}
                </span>
                <span className={`text-sm font-bold ${isToday ? 'text-coral' : 'text-navy'}`} data-id="element-1912">
                  {day.getDate()}
                </span>
              </div>
            </div>
            {/* Column body */}
            <div className="p-2 space-y-2 min-h-[120px]" data-id="element-1913">
              {dayJobs.length > 0 ? dayJobs.map(j => <JobChip key={j.id} job={j} onOpen={onOpen} onAssign={onAssign} onReminder={onReminder} data-id="element-1914" />) : <p className="text-[11px] text-muted-foreground text-center py-4" data-id="element-1915">
                  Nessun incarico
                </p>}
            </div>
          </div>;
    })}
    </div>;
}
// ==========================================================================
// MONTH calendar — 7-col grid with per-day count badges
// ==========================================================================
function MonthCalendar({
  monthDate,
  onPrevMonth,
  onNextMonth,
  datedJobs,
  today,
  selectedDay,
  onSelectDay,
  onOpen
}: {
  monthDate: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  datedJobs: {
    job: Job;
    date: Date;
  }[];
  today: Date;
  selectedDay: Date;
  onSelectDay: (d: Date) => void;
  onOpen: (j: Job) => void;
  onAssign: (j: Job) => void;
  onReminder: (id: string) => void;
}) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const leadingBlanks = firstDow === 0 ? 6 : firstDow - 1; // make Mon first
  const countsByDay = useMemo(() => {
    const m = new Map<number, number>();
    datedJobs.forEach(d => {
      if (d.date.getFullYear() === year && d.date.getMonth() === month) {
        m.set(d.date.getDate(), (m.get(d.date.getDate()) ?? 0) + 1);
      }
    });
    return m;
  }, [datedJobs, year, month]);
  const selectedJobs = datedJobs.filter(d => sameDay(d.date, selectedDay)).map(d => d.job).sort((a, b) => a.time.localeCompare(b.time));
  const cells: (number | null)[] = [...Array.from({
    length: leadingBlanks
  }, () => null), ...Array.from({
    length: daysInMonth
  }, (_, i) => i + 1)];
  return <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-1916">
      {/* LEFT: calendar */}
      <div data-id="element-1917">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-3" data-id="element-1918">
          <button onClick={onPrevMonth} aria-label="Mese precedente" className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-gray-300 transition-colors" data-id="element-1919">
            <ChevronLeft size={16} data-id="element-1920" />
          </button>
          <span className="text-sm font-semibold text-navy" data-id="element-1921">
            {MONTH_NAMES_FULL[month]} {year}
          </span>
          <button onClick={onNextMonth} aria-label="Mese successivo" className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-gray-300 transition-colors" data-id="element-1922">
            <ChevronRight size={16} data-id="element-1923" />
          </button>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-1924">
          {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => <div key={d} className="text-[10px] uppercase tracking-wider font-medium text-gray-400 text-center py-1" data-id="element-1925">
              {d}
            </div>)}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1" data-id="element-1926">
          {cells.map((dayNum, idx) => {
          if (dayNum === null) return <div key={`blank-${idx}`} className="aspect-square" data-id="element-1927" />;
          const date = new Date(year, month, dayNum);
          const count = countsByDay.get(dayNum) ?? 0;
          const isToday = sameDay(date, today);
          const isSelected = sameDay(date, selectedDay);
          return <button key={dayNum} onClick={() => onSelectDay(date)} className={`relative aspect-square rounded-lg border flex flex-col items-center justify-center transition-colors ${isSelected ? 'border-coral bg-coral/5' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1928">
                <span className={`text-sm font-semibold ${isToday ? 'text-coral' : 'text-navy'}`} data-id="element-1929">
                  {dayNum}
                </span>
                {count > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-coral text-white text-[9px] font-bold flex items-center justify-center" data-id="element-1930">
                    {count}
                  </span>}
              </button>;
        })}
        </div>
      </div>

      {/* RIGHT: agenda sidebar for the selected day */}
      <aside className="lg:border-l lg:border-border lg:pl-6" data-id="element-1931">
        <h2 className="text-sm font-semibold text-navy mb-3 flex items-center gap-2" data-id="element-1932">
          <CalendarDays size={16} className="text-coral" data-id="element-1933" />
          {DAY_NAMES_FULL[selectedDay.getDay()]} {selectedDay.getDate()}{' '}
          {MONTH_NAMES_FULL[selectedDay.getMonth()]}
        </h2>
        {selectedJobs.length > 0 ? <div className="space-y-2 lg:max-h-[480px] lg:overflow-y-auto lg:pr-1" data-id="element-1934">
            {selectedJobs.map(j => <JobChip key={j.id} job={j} onOpen={onOpen} onAssign={onAssign} onReminder={onReminder} data-id="element-1935" />)}
          </div> : <div className="bg-white border border-border rounded-xl p-6 text-center" data-id="element-1936">
            <p className="text-sm text-muted-foreground" data-id="element-1937">
              Nessun incarico in questo giorno.
            </p>
          </div>}
      </aside>
    </div>;
}
// ==========================================================================
// MAP — zone coverage for the selected period
// ==========================================================================
function ZoneCoverageView({
  jobs,
  periodLabel
}: {
  jobs: Job[];
  periodLabel: string;
}) {
  // Group jobs by zone
  const zones = useMemo(() => {
    const m = new Map<string, Job[]>();
    jobs.forEach(j => {
      const arr = m.get(j.zone) ?? [];
      arr.push(j);
      m.set(j.zone, arr);
    });
    return Array.from(m.entries()).map(([zone, list]) => ({
      zone,
      jobs: list,
      count: list.length
    })).sort((a, b) => b.count - a.count);
  }, [jobs]);
  const maxCount = zones.reduce((m, z) => Math.max(m, z.count), 0);
  const totalCount = jobs.length;
  if (zones.length === 0) {
    return <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-1938">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-1939">
          <MapIcon size={20} className="text-muted-foreground" data-id="element-1940" />
        </div>
        <p className="text-sm font-medium text-foreground" data-id="element-1941">
          Nessuna zona coperta
        </p>
        <p className="text-xs text-muted-foreground mt-1" data-id="element-1942">
          Non ci sono incarichi per {periodLabel.toLowerCase()} da mostrare
          sulla mappa.
        </p>
      </div>;
  }
  // Deterministic marker positions on the decorative map
  const positions = [{
    x: 26,
    y: 32
  }, {
    x: 62,
    y: 24
  }, {
    x: 44,
    y: 54
  }, {
    x: 74,
    y: 62
  }, {
    x: 20,
    y: 68
  }, {
    x: 56,
    y: 78
  }, {
    x: 82,
    y: 40
  }, {
    x: 34,
    y: 80
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-1943">
      {/* LEFT: large decorative coverage map */}
      <div className="relative w-full rounded-xl overflow-hidden border border-gray-200 bg-slate-100" data-id="element-1944">
        <svg viewBox="0 0 100 64" className="w-full h-72 sm:h-96 lg:h-[520px]" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-id="element-1945">
          <rect width="100" height="64" fill="#f1f5f9" data-id="element-1946" />
          {/* streets */}
          <path d="M0 16 H100 M0 32 H100 M0 48 H100 M20 0 V64 M44 0 V64 M64 0 V64 M82 0 V64" stroke="#d8e0e9" strokeWidth="1.2" data-id="element-1947" />
          {/* blocks */}
          <rect x="4" y="4" width="12" height="8" fill="#e2e8f0" data-id="element-1948" />
          <rect x="48" y="36" width="12" height="8" fill="#e2e8f0" data-id="element-1949" />
          <rect x="86" y="50" width="10" height="10" fill="#e2e8f0" data-id="element-1950" />
          {/* river-ish */}
          <path d="M0 60 Q30 50 50 58 T100 52" stroke="#cfe3f0" strokeWidth="3" fill="none" data-id="element-1951" />
        </svg>
        {/* Zone markers */}
        {zones.map((z, i) => {
        const pos = positions[i % positions.length];
        const isMax = z.count === maxCount && maxCount > 0;
        const size = 28 + Math.min(z.count, 6) * 4;
        return <div key={z.zone} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{
          left: `${pos.x}%`,
          top: `${pos.y}%`
        }} data-id="element-1952">
              <span className={`rounded-full border-2 border-white shadow-md flex items-center justify-center text-white font-bold ${isMax ? 'bg-coral' : 'bg-navy'}`} style={{
            width: size,
            height: size,
            fontSize: size > 36 ? 13 : 11
          }} title={`${shortZone(z.zone)} · ${z.count} interventi`} data-id="element-1953">
                {z.count}
              </span>
              <span className="mt-1 px-1.5 py-0.5 rounded bg-white/90 text-[10px] font-medium text-navy shadow-sm whitespace-nowrap" data-id="element-1954">
                {shortZone(z.zone)}
              </span>
            </div>;
      })}
        {/* Period chip */}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 text-[11px] font-medium text-navy shadow-sm" data-id="element-1955">
          <CalendarDays size={12} className="text-coral" data-id="element-1956" />
          {periodLabel}
        </span>
      </div>

      {/* RIGHT: lateral coverage breakdown */}
      <aside className="lg:border-l lg:border-border lg:pl-6" data-id="element-1957">
        {/* Coverage summary */}
        <div className="mb-3" data-id="element-1958">
          <h2 className="text-sm font-semibold text-navy" data-id="element-1959">
            Copertura zone · {periodLabel}
          </h2>
          <span className="text-xs text-muted-foreground" data-id="element-1960">
            {zones.length} {zones.length === 1 ? 'zona' : 'zone'} · {totalCount}{' '}
            {totalCount === 1 ? 'intervento' : 'interventi'}
          </span>
        </div>

        {/* Zone breakdown list */}
        <div className="space-y-2 lg:max-h-[480px] lg:overflow-y-auto lg:pr-1" data-id="element-1961">
          {zones.map(z => {
          const pct = maxCount > 0 ? z.count / maxCount * 100 : 0;
          const isMax = z.count === maxCount && maxCount > 0;
          return <div key={z.zone} className="bg-white border border-border rounded-xl p-3.5" data-id="element-1962">
                <div className="flex items-center justify-between gap-3 mb-2" data-id="element-1963">
                  <div className="flex items-center gap-2 min-w-0" data-id="element-1964">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isMax ? 'bg-coral/10 text-coral' : 'bg-navy/5 text-navy'}`} data-id="element-1965">
                      <MapPin size={15} data-id="element-1966" />
                    </span>
                    <span className="text-sm font-semibold text-navy truncate" data-id="element-1967">
                      {shortZone(z.zone)}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-navy flex-shrink-0" data-id="element-1968">
                    {z.count}{' '}
                    <span className="text-xs font-normal text-muted-foreground" data-id="element-1969">
                      {z.count === 1 ? 'intervento' : 'interventi'}
                    </span>
                  </span>
                </div>
                {/* Coverage bar */}
                <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-1970">
                  <div className={`h-1.5 rounded-full transition-all ${isMax ? 'bg-coral' : 'bg-navy'}`} style={{
                width: `${pct}%`
              }} data-id="element-1971" />
                </div>
              </div>;
        })}
        </div>
      </aside>
    </div>;
}